import React, { useState, useEffect } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Autocomplete,
    TextField, MenuItem, Box, Typography, Snackbar, Alert, FormControlLabel, Switch 
} from '@mui/material';
import * as API from '../../api';

const ACCOUNT_TYPES = [
    { value: 'COMPANY', label: 'Tài khoản Công ty' },
    { value: 'PERSONAL', label: 'Tài khoản Cá nhân' },
    { value: 'CASH', label: 'Tiền mặt' }
];

const CustomLabel = ({ title, required = false }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
        {title} {required && <span style={{ color: 'red' }}>*</span>}
    </Typography>
);

const FinancialAccountDialog = ({ open, onClose, initialData, onSuccess }) => {
    const [formData, setFormData] = useState({
        name: '', 
        account_type: 'COMPANY', 
        holder_name: '', 
        bank_name: '', 
        account_number: '',
        is_active: true
    });
    const [userOptions, setUserOptions] = useState([]);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const showToast = (msg, severity = 'success') => setToast({ open: true, message: msg, severity });
    const handleCloseToast = () => setToast({ ...toast, open: false });

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = await API.fetchUsers();
                setUserOptions(res.data);
            } catch (error) {
                console.error("Lỗi tải danh sách user:", error);
            }
        };
        fetchUsers();
    }, []);

    useEffect(() => {
        if (open) {
            if (initialData) {
                setFormData(initialData);
            } else {
                setFormData({
                    name: '', 
                    account_type: 'COMPANY', 
                    owner: null,
                    holder_name: '', 
                    bank_name: '', 
                    account_number: '',
                    is_active: true
                });
            }
        }
    }, [open, initialData]);

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async () => {
        if (!formData.name) {
            showToast("Vui lòng nhập tên gợi nhớ cho tài khoản", "error");
            return;
        }

        try {
            if (initialData) {
                await API.updateFinancialAccount(initialData.id, formData);
                showToast("Cập nhật thành công", "success");
            } else {
                await API.createFinancialAccount(formData);
                showToast("Thêm mới thành công", "success");
            }
            setTimeout(() => onSuccess(), 1000);
        } catch (error) {
            showToast("Có lỗi xảy ra: " + error.message, "error");
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#007373', color: 'white', fontWeight: 'bold' }}>
                    {initialData ? 'CẬP NHẬT TÀI KHOẢN' : 'THÊM MỚI TÀI KHOẢN'}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        
                        <Box>
                            <CustomLabel title="Tên gợi nhớ (Alias)" required />
                            <TextField 
                                name="name" size="small" fullWidth 
                                placeholder="VD: VCB Sếp Hùng"
                                value={formData.name} onChange={handleChange} 
                            />
                        </Box>

                        <Box>
                            <CustomLabel title="Loại tài khoản" />
                            <TextField 
                                select name="account_type" size="small" fullWidth
                                value={formData.account_type} onChange={handleChange}
                            >
                                {ACCOUNT_TYPES.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </TextField>
                        </Box>

                        <Box>
                            <CustomLabel title="Người sở hữu (Gắn với User hệ thống)" />
                            <Autocomplete
                                options={userOptions}
                                getOptionLabel={(option) => `${option.last_name} ${option.first_name} (${option.username})`}
                                value={userOptions.find(u => u.id === formData.owner) || null}
                                onChange={(event, newValue) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        owner: newValue ? newValue.id : null
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        size="small" 
                                    />
                                )}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box flex={1}>
                                <CustomLabel title="Ngân hàng" />
                                <TextField 
                                    name="bank_name" size="small" fullWidth 
                                    placeholder="VD: Vietcombank"
                                    value={formData.bank_name || ''} onChange={handleChange} 
                                />
                            </Box>
                            <Box flex={1}>
                                <CustomLabel title="Số tài khoản" />
                                <TextField 
                                    name="account_number" size="small" fullWidth 
                                    value={formData.account_number || ''} onChange={handleChange} 
                                />
                            </Box>
                        </Box>

                        <Box>
                            <CustomLabel title="Chủ tài khoản" />
                            <TextField 
                                name="holder_name" size="small" fullWidth 
                                value={formData.holder_name || ''} onChange={handleChange} 
                            />
                        </Box>

                        <Box>
                            <FormControlLabel 
                                control={<Switch checked={formData.is_active} onChange={handleChange} name="is_active" color="primary" />}
                                label="Đang hoạt động"
                            />
                        </Box>

                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={onClose} variant="outlined" color="inherit">Hủy</Button>
                    <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: '#007373' }}>Lưu dữ liệu</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={toast.open} autoHideDuration={2000} onClose={handleCloseToast} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', boxShadow: 3 }} variant="filled">{toast.message}</Alert>
            </Snackbar>
        </>
    );
};

export default FinancialAccountDialog;